import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, X, Trash2, Eye, EyeOff, Sparkles, Image as ImageIcon, ZoomIn, ZoomOut, Package, Edit3, Save, XCircle, ChevronDown, Check, ChevronRight, Zap, CheckCircle, AlertTriangle } from 'lucide-react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Connection, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { useActivity } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useWallet } from '../contexts/WalletContext';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import WalletRequired from '../components/WalletRequired';
import { createCapsule } from '../utils/supabaseDatabase';
import { supabase } from '../lib/supabase';
import { mintCapsulePieces, getExplorerUrl } from '../utils/solanaMinting';
import Logo3D from '../components/Logo3D';

// 3D Model Viewer Component - Memoized to prevent unnecessary re-renders
const ModelViewer = memo(({ file, index }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  useEffect(() => {
    if (!file || !canvasRef.current) return;

    let isMounted = true;
    
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.set(0, 0, 5);

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      canvas: canvasRef.current, 
      antialias: true,
      alpha: true,
      powerPreference: 'low-power' // Optimize for battery
    });
    renderer.setSize(200, 200);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Cap at 2x for performance
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.8);
    directionalLight.position.set(10, 10, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const pointLight = new THREE.PointLight(0xffffff, 1.0);
    pointLight.position.set(-10, -10, -5);
    scene.add(pointLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 1.0);
    fillLight.position.set(-5, 5, 5);
    scene.add(fillLight);

    const topLight = new THREE.DirectionalLight(0xffffff, 1.2);
    topLight.position.set(0, 15, 0);
    scene.add(topLight);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.enablePan = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 2;

    // Load GLB/GLTF file
    const loader = new GLTFLoader();
    const actualFile = file.file || file;
    const fileURL = URL.createObjectURL(actualFile);

    loader.load(
      fileURL,
      (gltf) => {
        if (!isMounted) return;
        
        const model = gltf.scene;
        
        // Center the model
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        
        // Scale model to fit in view
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        model.scale.setScalar(scale);
        
        // Add shadows and preserve original materials
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            // Preserve original material properties - DO NOT modify colors or materials
            if (child.material) {
              // Only mark material as needing update, but don't change any properties
              child.material.needsUpdate = true;
              
              // Note: We're preserving the original colors, metalness, roughness, and tone mapping
              // to ensure the model looks exactly as it was designed
            }
          }
        });
        
        scene.add(model);
        
        // Adjust camera to fit model
        const box2 = new THREE.Box3().setFromObject(model);
        const size2 = box2.getSize(new THREE.Vector3());
        const maxDim2 = Math.max(size2.x, size2.y, size2.z);
        camera.position.set(0, 0, maxDim2 * 2);
        camera.lookAt(0, 0, 0);
        controls.update();
        
        // Clean up URL
        URL.revokeObjectURL(fileURL);
      },
      undefined,
      (error) => {
        console.error('Error loading model:', error);
        URL.revokeObjectURL(fileURL);
      }
    );

    // Animation loop with cancellation
    const animate = () => {
      if (!isMounted) return;
      animationFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup function
    return () => {
      isMounted = false;
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Dispose renderer
      if (renderer) {
        renderer.dispose();
      }
      
      // Dispose controls
      if (controls) {
        controls.dispose();
      }
      
      // Dispose scene objects
      scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      
      URL.revokeObjectURL(fileURL);
    };
  }, [file, index]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full rounded-lg"
      style={{ width: '200px', height: '200px' }}
    />
  );
});

ModelViewer.displayName = 'ModelViewer';

const CreateCollection = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const { logCollectionCreated } = useActivity();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { publicKey } = useWallet();
  const solanaWallet = useSolanaWallet(); // Get the full Solana wallet adapter
  const [isCreating, setIsCreating] = useState(false);
  const [collectionModels, setCollectionModels] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [modelViewerKey, setModelViewerKey] = useState(0);
  const canvasRefs = useRef({});
    const [editingModel, setEditingModel] = useState(null);
      const [formData, setFormData] = useState({
     name: '',
     description: '',
     website: '',
     twitter: '',
     discord: '',
     instagram: '',
     banner: null
   });
  const [newTag, setNewTag] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState({});
  const categoryDropdownRefs = useRef({});
  const [otherCategories, setOtherCategories] = useState({}); // Track custom categories for each model
  const [walletBalance, setWalletBalance] = useState(null);
  
  // Minting progress modal state (like execute trading page)
  const [mintProgressModal, setMintProgressModal] = useState({
    show: false,
    step: 0,
    totalSteps: 0,
    currentMessage: 'Initializing minting...',
    isComplete: false,
    piecesProgress: [],
    totalSuccess: 0,
    totalFailed: 0,
    error: null
  });
  
  // Old minting modal states - keeping for compatibility
  const [showMintModal, setShowMintModal] = useState(false);
  const [createdCapsuleData, setCreatedCapsuleData] = useState(null);
  const [mintingProgress, setMintingProgress] = useState([]);
  const [isMinting, setIsMinting] = useState(false);
  const [mintResults, setMintResults] = useState(null);
  
  // Notification system
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  
  const showNotification = (message, type = 'error') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  const categories = [
    "Tops", "Bottoms", "Shoes", "Accessories", "Other"
  ];

  // Handle click outside category dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(categoryDropdownRefs.current).forEach(index => {
        const ref = categoryDropdownRefs.current[index];
        if (ref && !ref.contains(event.target)) {
          setCategoryDropdownOpen(prev => ({
            ...prev,
            [index]: false
          }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (publicKey && solanaWallet.connected) {
        try {
          // Use Helius mainnet RPC endpoint for better reliability
          const heliusKey = import.meta.env.VITE_HELIUS_API_KEY;
          const rpcEndpoint = heliusKey 
            ? `https://mainnet.helius-rpc.com/?api-key=${heliusKey}`
            : 'https://api.mainnet-beta.solana.com';
          
          const connection = new Connection(
            rpcEndpoint,
            'confirmed'
          );
          const balance = await connection.getBalance(publicKey);
          const balanceSOL = balance / LAMPORTS_PER_SOL;
          setWalletBalance(balanceSOL);
          console.log('💰 Wallet balance (mainnet):', balanceSOL.toFixed(4), 'SOL');
        } catch (error) {
          console.error('Error fetching wallet balance:', error);
          setWalletBalance(null);
        }
      } else {
        setWalletBalance(null);
      }
    };

    fetchBalance();
    // Refresh balance every 30 seconds
    const interval = setInterval(fetchBalance, 30000);
    
    return () => clearInterval(interval);
  }, [publicKey, solanaWallet.connected]);

  // Check if localStorage has enough space
  const checkStorageSpace = (dataSize) => {
    try {
      const testKey = '__storage_test__';
      const testData = 'x'.repeat(dataSize);
      localStorage.setItem(testKey, testData);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.banner) {
      showNotification('Please fill in all required fields including the banner image');
      return;
    }

    // Check if all social links are filled
    if (!formData.website || !formData.twitter || !formData.instagram) {
      showNotification('Please fill in all social links (Website, X/Twitter, and Instagram)');
      return;
    }

    if (!publicKey) {
      showNotification('Please connect your wallet to create a capsule');
      return;
    }
    
    if (collectionModels.length === 0) {
      showNotification('Please upload at least one 3D model');
      return;
    }

    // Check if all models have names
    const modelsWithoutNames = collectionModels.filter(model => !model.modelName.trim());
    if (modelsWithoutNames.length > 0) {
      showNotification('Please provide names for all pieces in your capsule');
      return;
    }

    // Check if all models have colors
    const modelsWithoutColors = collectionModels.filter(model => !model.modelColor.trim());
    if (modelsWithoutColors.length > 0) {
      showNotification('Please provide colors for all pieces in your capsule');
      return;
    }

    // Check if all models have categories
    const modelsWithoutCategories = collectionModels.filter(model => !model.modelCategory || !model.modelCategory.trim());
    if (modelsWithoutCategories.length > 0) {
      showNotification('Please provide categories for all pieces in your capsule');
      return;
    }

    // Check if models with "Other" category have custom category names
    const modelsWithOtherMissingCustom = collectionModels.filter((model, index) => 
      model.modelCategory === 'Other' && (!otherCategories[index] || !otherCategories[index].trim())
    );
    if (modelsWithOtherMissingCustom.length > 0) {
      showNotification('Please provide custom category names for pieces with "Other" category');
      return;
    }

    // Check if all models have prices
    const modelsWithoutPrices = collectionModels.filter(model => !model.modelPrice || parseFloat(model.modelPrice) <= 0);
    if (modelsWithoutPrices.length > 0) {
      showNotification('Please provide prices for all pieces in your capsule');
      return;
    }

    // Check if all models have quantities
    const modelsWithoutQuantities = collectionModels.filter(model => !model.modelQuantity || parseInt(model.modelQuantity) < 1);
    if (modelsWithoutQuantities.length > 0) {
      showNotification('Please provide quantities for all pieces in your capsule');
      return;
    }

    // Check if any quantity exceeds the maximum
    const modelsWithExcessQuantity = collectionModels.filter(model => parseInt(model.modelQuantity) > 50);
    if (modelsWithExcessQuantity.length > 0) {
      showNotification('Maximum quantity per piece is 50. Please reduce the quantity.');
      return;
    }

    // Calculate total pieces and warn for large batches
    const totalPieces = collectionModels.reduce((sum, model) => sum + parseInt(model.modelQuantity || 0), 0);
    if (totalPieces > 500) {
      const estimatedMinutes = Math.ceil((totalPieces * 1.5) / 60); // ~1.5 seconds per piece
      if (!confirm(`⚠️ Large Batch Warning\n\nYou are about to create ${totalPieces} total pieces.\n\nEstimated time: ${estimatedMinutes} minutes\n\nThis will require approving multiple blockchain transactions.\n\nDo you want to continue?`)) {
        return;
      }
    } else if (totalPieces > 200) {
      const estimatedMinutes = Math.ceil((totalPieces * 2) / 60); // ~2 seconds per piece
      if (!confirm(`You are about to create ${totalPieces} pieces. This will take approximately ${estimatedMinutes} minutes. Continue?`)) {
        return;
      }
    }

    // Check if all models have descriptions
    const modelsWithoutDescriptions = collectionModels.filter(model => !model.modelDescription || !model.modelDescription.trim());
    if (modelsWithoutDescriptions.length > 0) {
      showNotification('Please provide descriptions for all pieces in your capsule');
      return;
    }

    // Check if all models have thumbnails
    const modelsWithoutThumbnails = collectionModels.filter(model => !model.thumbnail || !model.thumbnail.dataUrl);
    if (modelsWithoutThumbnails.length > 0) {
      showNotification('Please upload thumbnail images for all pieces in your capsule');
      return;
    }

    setIsCreating(true);

    try {
      // Convert banner to base64 if present
      let bannerDataUrl = null;
      if (formData.banner) {
        bannerDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(formData.banner);
        });
      }

      console.log('🚀 Starting capsule creation with Supabase...');
      
      // Prepare capsule data
      const capsuleData = {
        name: formData.name,
        description: formData.description,
        website: formData.website,
        twitter: formData.twitter,
        discord: formData.discord,
        instagram: formData.instagram
      };

      // Prepare pieces data with File objects for Supabase upload
      const piecesData = collectionModels.map((model, index) => {
        const royaltyPct = Number(model.modelRoyaltyPercent ?? 5);
        const royaltyBps = Math.round(royaltyPct * 100);
        
        return {
          modelName: model.modelName,
          modelDescription: model.modelDescription,
          modelColor: model.modelColor,
          modelCategory: model.modelCategory === 'Other' ? (otherCategories[index] || '').trim() : model.modelCategory,
          modelPrice: model.modelPrice,
          modelQuantity: model.modelQuantity,
          modelRoyaltyBps: royaltyBps, // Convert to basis points
          modelTags: model.modelTags || [],
          file: model.file, // Keep the original File object for Supabase upload
          thumbnailFile: model.thumbnailFile || null // Thumbnail as File object
        };
      });

      console.log('Preparing to mint', piecesData.length, 'pieces...');
      console.log('📸 Piece thumbnail files:', piecesData.map(p => ({
        name: p.modelName,
        hasThumbnailFile: !!p.thumbnailFile,
        thumbnailFileName: p.thumbnailFile?.name
      })));
      console.log('🏷️ Piece tags:', piecesData.map(p => ({
        name: p.modelName,
        tags: p.modelTags
      })));
      
      // Immediately start minting all pieces (like CreatePiece page does)
      console.log('🎨 Starting capsule minting & creation process...');
      
      // Get wallet adapter and ensure it's properly connected
      const solanaWallet = window.solana || window.solflare;
      if (!solanaWallet) {
        throw new Error('No Solana wallet found. Please install Phantom or Solflare.');
      }

      console.log('🔌 Checking wallet connection...');
      console.log('Wallet isConnected:', solanaWallet.isConnected);
      console.log('Wallet publicKey:', solanaWallet.publicKey?.toString());

      // Force connect if needed
      try {
        if (!solanaWallet.isConnected || !solanaWallet.publicKey) {
          console.log('🔌 Wallet not connected, connecting now...');
          const response = await solanaWallet.connect();
          console.log('✅ Wallet connect response:', response);
        }
        
        // Wait a bit for connection to stabilize
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify connection
        if (!solanaWallet.publicKey) {
          throw new Error('Wallet connected but no public key available');
        }
        
        console.log('✅ Wallet ready:', solanaWallet.publicKey.toString());
      } catch (connectError) {
        console.error('❌ Wallet connection failed:', connectError);
        throw new Error('Please connect your wallet to mint NFTs');
      }
      
      // Calculate total quantity of all pieces to be minted
      const totalQuantity = piecesData.reduce((sum, piece) => {
        return sum + parseInt(piece.modelQuantity || 1);
      }, 0);

      // Show new progress modal
      setMintProgressModal({
        show: true,
        step: 0,
        totalSteps: totalQuantity,
        currentMessage: 'Preparing to mint pieces...',
        isComplete: false,
        piecesProgress: piecesData.map((piece, index) => ({
          index,
          name: piece.modelName,
          status: 'pending',
          mintAddress: null,
          signature: null,
          error: null
        })),
        totalSuccess: 0,
        totalFailed: 0,
        error: null
      });

      // Track minted pieces to create capsule at the end
      const mintedPieces = [];
      const { createPiece } = await import('../utils/supabaseDatabase.js');
      const { mintWithUserWallet } = await import('../utils/solanaMinting.js');
      const { mintEditions, mintSinglePiece } = await import('../utils/editionMinting.js');

      // Track total pieces minted so far (for progress)
      let totalMintedSoFar = 0;

      // Mint and create each piece
      for (let i = 0; i < piecesData.length; i++) {
        const pieceData = piecesData[i];
        let createdPiece = null; // Track if piece was created for cleanup on error
        
        try {
          console.log(`\n🎨 Minting piece ${i + 1}/${piecesData.length}: ${pieceData.modelName}`);
          
          // Ensure wallet is still connected before each piece
          if (!solanaWallet.isConnected || !solanaWallet.publicKey) {
            console.log('🔌 Wallet disconnected, attempting to reconnect...');
            try {
              await solanaWallet.connect();
              // Wait for connection to stabilize
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // Verify reconnection
              if (!solanaWallet.publicKey) {
                throw new Error('Wallet reconnected but no public key available');
              }
              
              console.log('✅ Wallet reconnected successfully');
            } catch (reconnectError) {
              console.error('❌ Reconnection failed:', reconnectError);
              throw new Error('Wallet disconnected. Please reconnect your wallet to continue.');
            }
          }
          
          // Update status: minting
          const pieceQuantity = parseInt(pieceData.modelQuantity) || 1;
          setMintProgressModal(prev => {
            const updatedProgress = [...prev.piecesProgress];
            updatedProgress[i] = { ...updatedProgress[i], status: 'minting' };
            const percentComplete = Math.round((totalMintedSoFar / totalQuantity) * 100);
            return {
              ...prev,
              step: totalMintedSoFar,
              currentMessage: totalQuantity > 100 
                ? `Minting piece ${totalMintedSoFar + 1}/${totalQuantity} (${percentComplete}%)`
                : `Minting piece ${totalMintedSoFar + 1}/${totalQuantity}...`,
              piecesProgress: updatedProgress
            };
          });

          // First, create the piece in database (like CreatePiece does)
          const pieceMetadata = {
            name: pieceData.modelName,
            category: pieceData.modelCategory,
            description: pieceData.modelDescription,
            color: pieceData.modelColor,
            price: parseFloat(pieceData.modelPrice),
            quantity: parseInt(pieceData.modelQuantity),
            royaltyBps: pieceData.modelRoyaltyBps,
            tags: pieceData.modelTags || []
          };

          createdPiece = await createPiece(
            pieceMetadata,
            pieceData.file,
        publicKey.toString(),
            pieceData.thumbnailFile,
            null
          );

          console.log(`✅ Piece ${i + 1} created in database`);

          // Verify wallet is ready before minting
          console.log(`🔍 Pre-mint wallet check for piece ${i + 1}:`);
          console.log('  - isConnected:', solanaWallet.isConnected);
          console.log('  - publicKey:', solanaWallet.publicKey?.toString());
          console.log('  - connected:', solanaWallet.connected);

          // Now mint the NFT (edition-aware)
          console.log(`🎨 Minting ${pieceQuantity === 1 ? 'single piece' : `${pieceQuantity} editions`} for capsule piece ${i + 1}...`);
          
          const mintData = {
            name: createdPiece.name,
            description: createdPiece.description || `${createdPiece.name} - 3D wearable piece`,
            image: createdPiece.poster_url || createdPiece.thumbnail_url,
            animation_url: createdPiece.preview_glb_url,
            category: createdPiece.category,
            price: createdPiece.price,
            attributes: [
              { trait_type: 'Category', value: createdPiece.category },
              { trait_type: 'Color', value: createdPiece.color || 'N/A' },
              ...(createdPiece.tags && createdPiece.tags.length > 0 ? [
                { trait_type: 'Tags', value: createdPiece.tags.join(', ') }
              ] : [])
            ],
            sellerFeeBps: createdPiece.royalty_bps || 500
          };

          let mintResult;
          
          if (pieceQuantity > 1) {
            // Multi-edition minting for this capsule piece
            console.log(`🎨 Minting ${pieceQuantity} editions for capsule piece...`);
            mintResult = await mintEditions(
              solanaWallet,
              mintData,
              pieceQuantity,
              createdPiece.id,
              (editionNum, total, result) => {
                console.log(`  ✅ Edition ${editionNum}/${total} minted`);
                totalMintedSoFar++;
                // Update progress for each edition minted
                setMintProgressModal(prev => ({
                  ...prev,
                  step: totalMintedSoFar,
                  currentMessage: `Minting piece ${totalMintedSoFar}/${totalQuantity}...`
                }));
              }
            );
            console.log(`✅ Piece ${i + 1} minted: ${mintResult.totalMinted} editions`);
          } else {
            // Single piece minting (1/1) - most capsule pieces
            mintResult = await mintSinglePiece(solanaWallet, mintData, createdPiece.id);
            console.log(`✅ Piece ${i + 1} minted:`, mintResult.mintAddress);
            
            // Log delegate status for debugging
            if (mintResult.delegateActive) {
              console.log(`  ✅ Delegate approved - piece should be auto-listed`);
            } else {
              console.warn(`  ⚠️ Delegate approval failed - piece will need manual listing`);
            }
            
            totalMintedSoFar++;
            // Update progress for single piece
            setMintProgressModal(prev => ({
              ...prev,
              step: totalMintedSoFar,
              currentMessage: `Minting piece ${totalMintedSoFar}/${totalQuantity}...`
            }));
            
            // NOTE: No need to call updatePieceWithMintAddress here
            // mintSinglePiece already calls it internally with the correct delegate info
          }

          console.log(`✅ Piece ${i + 1} completed`);

          // Track successful mint - handle both single pieces and multi-edition pieces
          if (pieceQuantity > 1) {
            // For multi-edition pieces, add ALL edition piece records (not just the master)
            // mintResult.pieces contains all the minted editions with their individual IDs
            if (mintResult.pieces && mintResult.pieces.length > 0) {
              console.log(`📦 Adding ${mintResult.pieces.length} edition pieces to capsule...`);
              mintResult.pieces.forEach((editionPiece) => {
                mintedPieces.push({
                  id: editionPiece.pieceId || editionPiece.id,
                  mint_address: editionPiece.mintAddress,
                  name: createdPiece.name,
                  category: createdPiece.category,
                  price: createdPiece.price
                });
              });
            } else {
              // Fallback: just add the master piece if mintResult.pieces is missing
              console.warn('⚠️ mintResult.pieces is empty, adding master piece only');
              mintedPieces.push({
                ...createdPiece,
                mint_address: `${mintResult.totalMinted} editions`
              });
            }
          } else {
            // Single piece (1/1) - just add the one piece
            mintedPieces.push({
              ...createdPiece,
              mint_address: mintResult.mintAddress
            });
          }

          // Update status: success
          const actualMintedCount = pieceQuantity > 1 ? mintResult.totalMinted : 1;
          
          // Check delegate status for single pieces
          const delegateActive = pieceQuantity === 1 ? mintResult.delegateActive : true;
          const needsManualListing = pieceQuantity === 1 && !delegateActive;
          
          setMintProgressModal(prev => {
            const updatedProgress = [...prev.piecesProgress];
            updatedProgress[i] = {
              ...updatedProgress[i],
              status: 'success',
              mintAddress: pieceQuantity === 1 ? mintResult.mintAddress : `${mintResult.totalMinted} editions`,
              signature: pieceQuantity === 1 ? mintResult.mintSignature : `${mintResult.totalMinted} mints`,
              isMultiEdition: pieceQuantity > 1,
              totalMinted: actualMintedCount,
              delegateActive: delegateActive,
              needsManualListing: needsManualListing
            };
            return {
              ...prev,
              piecesProgress: updatedProgress,
              totalSuccess: prev.totalSuccess + actualMintedCount
            };
          });

          // Small delay between mints
          if (i < piecesData.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }

        } catch (error) {
          console.error(`❌ Failed to mint piece ${i + 1}:`, error);
          
          // If piece was created but minting failed, delete it from database
          if (createdPiece && createdPiece.id) {
            console.log(`🗑️ Deleting piece ${i + 1} from database (mint failed)...`);
            try {
              const { supabase } = await import('../lib/supabase');
              
              // Delete the piece record
              await supabase
                .from('pieces')
                .delete()
                .eq('id', createdPiece.id);
              
              console.log(`✅ Piece ${i + 1} deleted from database`);
            } catch (deleteError) {
              console.error(`❌ Error deleting piece ${i + 1}:`, deleteError);
            }
          }
          
          // Update status: error - count the full quantity as failed
          const failedQuantity = parseInt(pieceData.modelQuantity) || 1;
          setMintProgressModal(prev => {
            const updatedProgress = [...prev.piecesProgress];
            updatedProgress[i] = {
              ...updatedProgress[i],
              status: 'error',
              error: error.message
            };
            return {
              ...prev,
              piecesProgress: updatedProgress,
              totalFailed: prev.totalFailed + failedQuantity
            };
          });
        }
      }

      // Create capsule record linking the minted pieces
      if (mintedPieces.length > 0) {
        console.log(`\n📦 Creating capsule with ${mintedPieces.length} minted pieces...`);
        
        const { supabase } = await import('../lib/supabase');
        const { getOrCreateCreator } = await import('../utils/supabaseDatabase.js');
        
        // Get or create creator
        const creator = await getOrCreateCreator(publicKey.toString());
        
        // Upload banner if provided
        let bannerUrl = null;
        if (formData.banner) {
          const bannerFileName = `banner-${Date.now()}-${formData.banner.name}`;
          const bucketName = 'nft-previews';
          
          const { data: bannerUpload, error: bannerError } = await supabase.storage
            .from(bucketName)
            .upload(bannerFileName, formData.banner);
          
          if (!bannerError) {
            const { data: { publicUrl } } = supabase.storage
              .from(bucketName)
              .getPublicUrl(bannerFileName);
            bannerUrl = publicUrl;
          }
        }
        
        // Create capsule record
        const capsuleInsert = {
          name: capsuleData.name,
          description: capsuleData.description,
          creator_id: creator.id,
          piece_count: mintedPieces.length,
          banner_url: bannerUrl,
          dropped_at: new Date().toISOString() // Set the drop date to now
        };
        
        // Only add optional social fields if they have values
        if (capsuleData.website) capsuleInsert.website = capsuleData.website;
        if (capsuleData.twitter) capsuleInsert.twitter = capsuleData.twitter;
        if (capsuleData.discord) capsuleInsert.discord = capsuleData.discord;
        if (capsuleData.instagram) capsuleInsert.instagram = capsuleData.instagram;
        
        console.log('📦 Inserting capsule:', capsuleInsert);
        
        const { data: newCapsule, error: capsuleError } = await supabase
          .from('capsules')
          .insert(capsuleInsert)
          .select()
          .maybeSingle();
        
        if (capsuleError) {
          console.error('❌ Error creating capsule:', capsuleError);
          console.error('Error details:', capsuleError.message, capsuleError.details, capsuleError.hint);
          throw capsuleError;
        }
        
        // Link pieces to capsule in pieces table
        const { error: linkError } = await supabase
          .from('pieces')
          .update({ capsule_id: newCapsule.id })
          .in('id', mintedPieces.map(p => p.id));
        
        if (linkError) {
          console.error('Error linking pieces to capsule in pieces table:', linkError);
        }
        
        // Also insert into capsule_pieces junction table (required for queries)
        const capsulePieceRecords = mintedPieces.map(piece => ({
          capsule_id: newCapsule.id,
          piece_id: piece.id
        }));
        
        const { error: junctionError } = await supabase
          .from('capsule_pieces')
          .insert(capsulePieceRecords);
        
        if (junctionError) {
          console.error('Error inserting into capsule_pieces junction table:', junctionError);
        } else {
          console.log('✅ Successfully inserted', capsulePieceRecords.length, 'records into capsule_pieces table');
        }
        
        const createdCapsule = {
          ...newCapsule,
          pieces: mintedPieces
        };

        console.log('✅ Capsule created:', createdCapsule);
        setCreatedCapsuleData(createdCapsule);
      
        // Log capsule creation to database (wallet_activity table)
        try {
          console.log('📝 Logging capsule creation to database...');
          const { error: activityError } = await supabase
            .from('wallet_activity')
            .insert({
              wallet: publicKey.toString(),
              activity_type: 'capsule_created',
              metadata: {
                capsule_id: createdCapsule.id,
                capsule_name: createdCapsule.name,
                piece_count: mintedPieces.length,
                image: bannerUrl,
                title: createdCapsule.name,
                description: `New capsule with ${mintedPieces.length} pieces`
              }
            });
          
          if (activityError) {
            console.error('❌ Error logging capsule creation to database:', activityError);
          } else {
            console.log('✅ Capsule creation logged to database');
          }
        } catch (error) {
          console.error('❌ Exception logging capsule creation:', error);
        }
      
      // Log collection creation activity (local state)
      logCollectionCreated({
        id: createdCapsule.id,
        name: createdCapsule.name,
          pieceCount: mintedPieces.length,
          image: bannerUrl
        });
      }

      // Set final results and show completion
      setMintProgressModal(prev => ({
        ...prev,
        isComplete: true,
        currentMessage: 'All pieces minted successfully!'
      }));
      
      console.log(`\n✅ Capsule minting complete!`);
      console.log(`   Successful: ${mintedPieces.length}/${piecesData.length}`);
      
    } catch (error) {
      console.error('Error creating/minting capsule:', error);
      
      // Show error in progress modal
      setMintProgressModal(prev => ({
        ...prev,
        isComplete: false,
        error: error.message || 'Failed to create/mint capsule. Please try again.'
      }));
    } finally {
      setIsCreating(false);
    }
  };

  // Handler for closing minting modal
  const handleCloseMintModal = () => {
    setShowMintModal(false);
    setMintingProgress([]);
    setMintResults(null);
      
             // Reset form
      setFormData({
        name: '',
        description: '',
        website: '',
        twitter: '',
        discord: '',
        instagram: '',
        banner: null
      });
      setCollectionModels([]);
      setModelViewerKey(prev => prev + 1);
      setNewTag('');
      
    // Navigate to created page
      navigate('/profile/created');
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 100MB for banner)
      if (file.size > 100 * 1024 * 1024) {
        showNotification('Banner image is too large. File size must be less than 100MB');
        return;
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        showNotification('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
        return;
      }

      // Reset previous banner state first
      setFormData(prev => ({
        ...prev,
        banner: null
      }));

      // Set new banner file
      setFormData(prev => ({
        ...prev,
        banner: file
      }));
    } else {
      // If no file selected, reset the banner state
      setFormData(prev => ({
        ...prev,
        banner: null
      }));
    }
  };

  const handleBannerDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-white', 'bg-[#0a0a0a]');
  };

  const handleBannerDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-white', 'bg-[#0a0a0a]');
  };

  const handleBannerDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-white', 'bg-[#0a0a0a]');
    
    const file = e.dataTransfer.files[0];
    if (!file) return;

    // Check file size (limit to 100MB for banner)
    if (file.size > 100 * 1024 * 1024) {
      showNotification('Banner image is too large. File size must be less than 100MB');
      return;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showNotification('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    setFormData(prev => ({
      ...prev,
      banner: file
    }));
  };

  const removeBanner = () => {
    setFormData(prev => ({
      ...prev,
      banner: null
    }));
    // Clear the file input
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  const triggerBannerUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    bannerInputRef.current?.click();
  };



  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (collectionModels.length + files.length > 20) {
      showNotification(`You can only upload up to 20 files. You currently have ${collectionModels.length} files.`);
      return;
    }

    files.forEach(file => {
      console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (limit to 50MB)
      if (file.size > 50 * 1024 * 1024) {
        showNotification(`File ${file.name} is too large. File size must be less than 50MB`);
        return;
      }
      
      // Check file type - ONLY GLB files are accepted (not GLTF)
      const fileName = file.name.toLowerCase();
      const isValidType = fileName.endsWith('.glb') || 
                         file.type === 'model/gltf-binary' ||
                         (file.type === '' && fileName.endsWith('.glb')); // Some GLB files have empty MIME type
      
      if (!isValidType) {
        showNotification(`Only GLB files are supported! File "${file.name}" is not a valid GLB file. Please upload .glb files only (not GLTF).`);
        return;
      }
      
      // Add file to capsule with default metadata
      const modelWithMetadata = {
        file: file, // Keep the original file object
        modelName: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
        modelDescription: '',
        modelColor: '',
        modelCategory: '',
        modelPrice: '',
        modelQuantity: '1',
        modelRoyaltyPercent: '0', // Default 0% royalty
        modelTags: []
      };
      
      setCollectionModels(prev => [...prev, modelWithMetadata]);
      setModelViewerKey(prev => prev + 1);
      console.log('File uploaded successfully:', file.name);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-white', 'bg-[#0a0a0a]');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-white', 'bg-[#0a0a0a]');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-white', 'bg-[#0a0a0a]');
    
    const files = Array.from(e.dataTransfer.files);
    
    if (collectionModels.length + files.length > 20) {
      showNotification(`You can only upload up to 20 files. You currently have ${collectionModels.length} files.`);
      return;
    }

    files.forEach(file => {
      console.log('File dropped:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (limit to 50MB)
      if (file.size > 50 * 1024 * 1024) {
        showNotification(`File ${file.name} is too large. File size must be less than 50MB`);
        return;
      }
      
      // Check file type - ONLY GLB files are accepted (not GLTF)
      const fileName = file.name.toLowerCase();
      const isValidType = fileName.endsWith('.glb') || 
                         file.type === 'model/gltf-binary' ||
                         (file.type === '' && fileName.endsWith('.glb')); // Some GLB files have empty MIME type
      
      if (!isValidType) {
        showNotification(`Only GLB files are supported! File "${file.name}" is not a valid GLB file. Please upload .glb files only (not GLTF).`);
        return;
      }
      
      // Add file to capsule with default metadata
      const modelWithMetadata = {
        file: file, // Keep the original file object
        modelName: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
        modelDescription: '',
        modelColor: '',
        modelCategory: '',
        modelPrice: '',
        modelQuantity: '1',
        modelRoyaltyPercent: '0', // Default 0% royalty
        modelTags: [],
        thumbnail: null // Add thumbnail state
      };
      
      // Add file to capsule
      setCollectionModels(prev => [...prev, modelWithMetadata]);
      setModelViewerKey(prev => prev + 1);
      console.log('File dropped successfully:', file.name);
    });
  };

  const removeModel = (index) => {
    setCollectionModels(prev => prev.filter((_, i) => i !== index));
    setModelViewerKey(prev => prev + 1);
  };

  const handleModelEdit = (index) => {
    setEditingModel(index);
  };

  const handleModelSave = (index) => {
    setEditingModel(null);
  };

  const handleModelCancel = useCallback(() => {
    setEditingModel(null);
  }, []);

  const handleModelFieldChange = useCallback((index, field, value) => {
    // Validate quantity field - prevent values > 50
    if (field === 'modelQuantity') {
      const numValue = parseInt(value);
      if (value !== '' && (!isNaN(numValue) && numValue > 50)) {
        showNotification('Maximum quantity is 50 pieces');
        return; // Don't update state
      }
    }
    
    setCollectionModels(prev => prev.map((model, i) => 
      i === index ? { ...model, [field]: value } : model
    ));
  }, []);

  // Handle category selection
  const handleCategorySelect = useCallback((index, category) => {
    setCollectionModels(prev => prev.map((model, i) => 
      i === index ? { ...model, modelCategory: category } : model
    ));
    setCategoryDropdownOpen(prev => ({
      ...prev,
      [index]: false
    }));
    // Clear custom category if switching away from "Other"
    if (category !== 'Other') {
      setOtherCategories(prev => {
        const newCategories = { ...prev };
        delete newCategories[index];
        return newCategories;
      });
    }
  }, []);

  // Thumbnail upload handlers
  const handleThumbnailUpload = useCallback((index, file) => {
    if (file) {
      console.log('Thumbnail selected for piece', index, ':', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (limit to 10MB for thumbnails)
      if (file.size > 10 * 1024 * 1024) {
        showNotification('Thumbnail file size must be less than 10MB');
        return;
      }
      
      // Check file type - only allow image files
      const isValidType = file.type.startsWith('image/');
      
      if (!isValidType) {
        showNotification('Please upload an image file for the thumbnail. Selected file type: ' + file.type);
        return;
      }
      
      // Create thumbnail object with both File and dataUrl
      const thumbnail = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        dataUrl: null
      };
      
      // Convert to base64 for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        const updatedThumbnail = { ...thumbnail, dataUrl };
        
        // Update the model with both thumbnail preview and File object
        setCollectionModels(prev => prev.map((model, i) => 
          i === index ? { 
            ...model, 
            thumbnail: updatedThumbnail,
            thumbnailFile: file // Store the actual File object for upload
          } : model
        ));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const removeThumbnail = useCallback((index) => {
    setCollectionModels(prev => prev.map((model, i) => 
      i === index ? { ...model, thumbnail: null, thumbnailFile: null } : model
    ));
  }, []);

  // Individual model tag management functions
  const addModelTag = useCallback((modelIndex) => {
    const tag = newTag.trim().toLowerCase();
    const currentModel = collectionModels[modelIndex];
    if (tag && !currentModel.modelTags.includes(tag) && currentModel.modelTags.length < 5) {
      setCollectionModels(prev => prev.map((model, i) => 
        i === modelIndex 
          ? { ...model, modelTags: [...model.modelTags, tag] }
          : model
      ));
      setNewTag('');
    } else if (currentModel.modelTags.length >= 5) {
      showNotification('You can only add up to 5 tags per piece');
    }
  }, [collectionModels, newTag]);

  const removeModelTag = useCallback((modelIndex, tagToRemove) => {
    setCollectionModels(prev => prev.map((model, i) => 
      i === modelIndex 
        ? { ...model, modelTags: model.modelTags.filter(tag => tag !== tagToRemove) }
        : model
    ));
  }, []);

  const handleModelTagKeyPress = useCallback((e, modelIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentModel = collectionModels[modelIndex];
      if (currentModel.modelTags.length < 5) {
        addModelTag(modelIndex);
      }
    }
  }, [collectionModels, addModelTag]);

  const triggerFileUpload = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  const handleUploadAreaClick = useCallback((e) => {
    // Only trigger file upload if clicking on the upload area itself, not on buttons or other elements
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) {
      return;
    }
    triggerFileUpload(e);
  }, [triggerFileUpload]);

  return (
    <WalletRequired 
      message="You need to connect your wallet to create and mint collections."
      action="create collections"
    >
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-12 text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Create Capsule</h1>
            <p className="text-gray-400">
              Upload and manage up to 20 3D models to create your exclusive capsule. 
              Each model will be part of your unique digital fashion capsule.
            </p>
          </div>

          {/* Main Form Container */}
          <div className="bg-[#111111] rounded-3xl shadow-xl border border-[#1a1a1a] overflow-hidden">
            <div className="px-8 py-6">
              <h2 className="text-xl font-semibold text-white">Capsule Information</h2>
              <p className="text-gray-400 mt-1">Fill in the details below to create your capsule</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Capsule Information</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Capsule Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                  placeholder="Enter capsule name"
                  autoComplete="off"
                  style={{ position: 'relative', zIndex: 10 }}
                />
              </div>


              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 resize-none"
                                      placeholder="Describe your capsule..."
                  autoComplete="off"
                  style={{ position: 'relative', zIndex: 10 }}
                />
              </div>



              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Payout Wallet
                </label>
                <div className="px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white">
                  {publicKey ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                      <span className="text-xs text-green-400 bg-green-400/20 px-2 py-1 rounded-full">
                        Connected
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500">Please connect your wallet</span>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  Payments will be sent to your connected wallet address
                </p>
              </div>
            </div>

            {/* Banner Upload */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Capsule Banner</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Banner Image <span className="text-red-400">*</span>
                </label>
                <p className="text-gray-400 text-sm mb-4">
                  Upload a banner image to showcase your capsule. 
                  Recommended size: 1400x400px. Supports JPG, PNG, GIF, and WebP formats up to 100MB.
                </p>
                
                {/* Banner Upload Area */}
                {!formData.banner ? (
                  <div 
                    onDragOver={handleBannerDragOver}
                    onDragLeave={handleBannerDragLeave}
                    onDrop={handleBannerDrop}
                    onClick={triggerBannerUpload}
                    className="border-2 border-dashed border-[#2a2a2a] rounded-2xl p-8 text-center transition-all duration-200 hover:border-white hover:bg-[#0a0a0a] cursor-pointer"
                  >
                    <div className="space-y-4">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto overflow-hidden">
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="Banner Upload" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-lg mb-2">Upload Banner Image <span className="text-red-400">*</span></p>
                        <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                          Drag and drop an image here or click to browse
                          <br />
                          <span className="text-white font-medium">Banner image is required</span>
                        </p>
                        <button
                          type="button"
                          onClick={triggerBannerUpload}
                          className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                          Choose Image
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Banner Preview */}
                    <div className="relative">
                      <img
                        src={URL.createObjectURL(formData.banner)}
                        alt="Banner preview"
                        className="w-full h-48 object-cover rounded-xl border border-[#2a2a2a]"
                      />
                      <button
                        type="button"
                        onClick={removeBanner}
                        className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-200 hover:scale-105"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    
                    {/* Banner Details */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">Banner Details</span>
                        <button
                          type="button"
                          onClick={triggerBannerUpload}
                          className="px-4 py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg transition-all duration-200 text-sm font-medium hover:scale-105"
                        >
                          Change Image
                        </button>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-gray-400">
                          <span>File:</span>
                          <span className="text-white">{formData.banner.name}</span>
                        </div>
                        <div className="flex justify-between text-gray-400">
                          <span>Size:</span>
                          <span className="text-white">{(formData.banner.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                        <div className="flex justify-between text-gray-400">
                          <span>Type:</span>
                          <span className="text-white">{formData.banner.type}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hidden File Input */}
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Social Links */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Social Links</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Website <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="https://yourwebsite.com"
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    X (Twitter) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="https://x.com/username"
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Instagram <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="https://instagram.com/username"
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                </div>
              </div>
            </div>

            {/* 3D Models Upload */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">3D Models ({collectionModels.length}/20)</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  3D Model Files <span className="text-red-400">*</span>
                </label>
                
                {/* Upload Area */}
                {collectionModels.length < 20 && (
                  <div 
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleUploadAreaClick}
                    className="border-2 border-dashed border-[#2a2a2a] rounded-2xl p-8 text-center transition-all duration-200 hover:border-white hover:bg-[#0a0a0a] cursor-pointer"
                  >
                    <div className="space-y-4">
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto overflow-hidden">
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="3D Models Upload" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-lg mb-2">Upload 3D models</p>
                        <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                          <span className="text-white font-semibold">Only GLB files accepted</span> (up to 50MB)<br/>
                          Drag and drop or click to browse.
                          <br />
                          <span className="text-white font-medium">
                            {20 - collectionModels.length} files remaining
                          </span>
                        </p>
                        <button
                          type="button"
                          onClick={triggerFileUpload}
                          className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                          Choose Files
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".glb,model/gltf-binary"
                  onChange={handleFileUpload}
                  className="hidden"
                  title="Only GLB files are supported"
                />

                {/* Uploaded Models Grid */}
                {collectionModels.length > 0 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {collectionModels.map((model, index) => (
                        <div key={index} className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                          {/* 3D Model Preview */}
                          <div className="space-y-3 mb-4">
                                                         <h4 className="text-white font-medium text-center text-sm truncate max-w-full px-2">
                               {model.file.name}
                             </h4>
                            <div className="space-y-3">
                              {/* Thumbnail Preview */}
                              {model.thumbnail?.dataUrl && (
                                <div className="text-center">
                                  <label className="block text-xs font-medium text-gray-400 mb-2">Thumbnail Preview</label>
                                  <div className="w-32 h-32 rounded-lg overflow-hidden border border-[#3a3a3a] mx-auto">
                                    <img 
                                      src={model.thumbnail.dataUrl} 
                                      alt="Piece thumbnail"
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {/* 3D Model Preview */}
                              <div className="text-center">
                                <label className="block text-xs font-medium text-gray-400 mb-2">3D Model Preview</label>
                                <div className="flex justify-center">
                                  <ModelViewer key={`${modelViewerKey}-${index}`} file={model} index={index} />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Model Metadata - Editable Fields */}
                          <div className="space-y-3 mb-4">
                                                         {/* Piece Name */}
                             <div className="space-y-1">
                               <label className="block text-xs font-medium text-gray-400">
                                 Piece Name <span className="text-red-400">*</span>
                               </label>
                              {editingModel === index ? (
                                                                 <input
                                   type="text"
                                   value={model.modelName || ''}
                                   onChange={(e) => handleModelFieldChange(index, 'modelName', e.target.value)}
                                   className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                   placeholder="Enter piece name"
                                   maxLength={50}
                                 />
                              ) : (
                                                                 <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                   <span 
                                     className="truncate w-full text-center cursor-help" 
                                     title={model.modelName || 'No name set'}
                                   >
                                     {model.modelName || 'No name set'}
                                   </span>
                                 </div>
                              )}
                            </div>

                            {/* Thumbnail Upload */}
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-400">
                                Thumbnail Image <span className="text-red-400">*</span>
                              </label>
                              <div className="space-y-2">
                                {model.thumbnail?.dataUrl ? (
                                  <div className="relative">
                                    <img 
                                      src={model.thumbnail.dataUrl} 
                                      alt="Thumbnail preview"
                                      className="w-full h-32 object-cover rounded-lg border border-[#3a3a3a]"
                                    />
                                    <button
                                      onClick={() => removeThumbnail(index)}
                                      className="absolute top-1 right-1 p-1 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                                    >
                                      <X size={12} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="border-2 border-dashed border-[#3a3a3a] rounded-lg p-4 text-center hover:border-white transition-colors">
                                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-2 overflow-hidden">
                                      <img 
                                        src="/images/piece-placeholder.png" 
                                        alt="Thumbnail Upload" 
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                    <p className="text-xs text-gray-400 mb-1">Upload thumbnail (required)</p>
                                    <p className="text-xs text-gray-500 mb-2">512x512px recommended</p>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleThumbnailUpload(index, e.target.files[0])}
                                      className="hidden"
                                      id={`thumbnail-${index}`}
                                    />
                                    <label
                                      htmlFor={`thumbnail-${index}`}
                                      className="inline-block px-3 py-1 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white text-xs rounded-lg cursor-pointer transition-colors"
                                    >
                                      Choose Image
                                    </label>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Model Description */}
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-400">
                                Description <span className="text-red-400">*</span>
                              </label>
                              {editingModel === index ? (
                                <textarea
                                  value={model.modelDescription || ''}
                                  onChange={(e) => handleModelFieldChange(index, 'modelDescription', e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent resize-none"
                                  placeholder="Describe this model... (required)"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                  {model.modelDescription || 'No description'}
                                </div>
                              )}
                            </div>

                            {/* Model Color */}
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-400">
                                Color <span className="text-red-400">*</span>
                              </label>
                              {editingModel === index ? (
                                <input
                                  type="text"
                                  value={model.modelColor || ''}
                                  onChange={(e) => handleModelFieldChange(index, 'modelColor', e.target.value)}
                                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                  placeholder="e.g., Red, Blue, Black, etc."
                                  required
                                />
                              ) : (
                                <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                  {model.modelColor || 'No color set'}
                                </div>
                              )}
                            </div>

                            {/* Model Category */}
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-400">
                                Category <span className="text-red-400">*</span>
                              </label>
                              {editingModel === index ? (
                                <div className="relative" ref={el => categoryDropdownRefs.current[index] = el}>
                                  <button
                                    type="button"
                                    onClick={() => setCategoryDropdownOpen(prev => ({
                                      ...prev,
                                      [index]: !prev[index]
                                    }))}
                                    className="w-full flex items-center justify-between bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#4a4a4a] cursor-pointer"
                                  >
                                    <span className={model.modelCategory ? 'text-white' : 'text-gray-500'}>
                                      {model.modelCategory || 'Select category'}
                                    </span>
                                    <ChevronDown 
                                      size={14} 
                                      className={`text-gray-400 transition-transform duration-200 ${categoryDropdownOpen[index] ? 'rotate-180' : ''}`} 
                                    />
                                  </button>
                                  
                                  {categoryDropdownOpen[index] && (
                                    <div className="absolute top-full left-0 mt-1 w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg shadow-xl z-50 overflow-hidden">
                                      {categories.map((category) => (
                                        <button
                                          key={category}
                                          type="button"
                                          onClick={() => handleCategorySelect(index, category)}
                                          className={`w-full px-3 py-2 text-left transition-all duration-200 flex items-center justify-between text-sm ${
                                            category === model.modelCategory
                                              ? 'bg-white/10 text-white'
                                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                                          }`}
                                        >
                                          <span>{category}</span>
                                          {category === model.modelCategory && (
                                            <Check size={14} className="text-white" />
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                  {model.modelCategory || 'No category set'}
                                </div>
                              )}
                            </div>

                            {/* Custom Category Input (shown when "Other" is selected) */}
                            {editingModel === index && model.modelCategory === 'Other' && (
                              <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-400">
                                  Custom Category <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={otherCategories[index] || ''}
                                  onChange={(e) => setOtherCategories(prev => ({ ...prev, [index]: e.target.value }))}
                                  maxLength={30}
                                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                  placeholder="Enter custom category"
                                />
                                <p className="text-xs text-gray-500">{(otherCategories[index] || '').length}/30 characters</p>
                              </div>
                            )}

                                                                                       {/* Model Price */}
                              <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-400">
                                  Price (SOL) <span className="text-red-400">*</span>
                                </label>
                                                               {editingModel === index ? (
                                  <div className="space-y-2">
                                    <input
                                      type="number"
                                      value={model.modelPrice || ''}
                                      onChange={(e) => handleModelFieldChange(index, 'modelPrice', e.target.value)}
                                      step="0.001"
                                      min="0"
                                      className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                      placeholder="0.1"
                                    />
                                    {/* USD Conversion Display */}
                                    {model.modelPrice && parseFloat(model.modelPrice) > 0 && (
                                      <div className="text-xs text-gray-400 text-center bg-[#1a1a1a] px-2 py-1 rounded border border-[#3a3a3a]">
                                        ≈ ${(parseFloat(model.modelPrice) * solPrice).toFixed(2)} USD
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                 <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                   {model.modelPrice ? `${model.modelPrice} SOL` : 'No price set'}
                                 </div>
                               )}
                             </div>

                             {/* Model Quantity */}
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-400">
                                Quantity <span className="text-red-400">*</span>
                              </label>
                              {editingModel === index ? (
                                <input
                                  type="number"
                                  value={model.modelQuantity || ''}
                                  onChange={(e) => handleModelFieldChange(index, 'modelQuantity', e.target.value)}
                                  min="1"
                                  max="50"
                                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                  placeholder="1"
                                />
                              ) : (
                                                                  <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                   {model.modelQuantity || '1'}
                                 </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">Max: 50</p>
                            </div>

                             {/* Model Royalty */}
                             <div className="space-y-1">
                               <label className="block text-xs font-medium text-gray-400">
                                 Creator Royalties (%) <span className="text-red-400">*</span>
                               </label>
                               {editingModel === index ? (
                                 <div className="space-y-1">
                                   <input
                                     type="number"
                                     value={model.modelRoyaltyPercent !== undefined ? model.modelRoyaltyPercent : '0'}
                                     onChange={(e) => handleModelFieldChange(index, 'modelRoyaltyPercent', e.target.value)}
                                     step="0.1"
                                     min="0"
                                     max="10"
                                     className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                     placeholder="0"
                                   />
                                   {model.modelRoyaltyPercent !== undefined && (Number(model.modelRoyaltyPercent) < 0 || Number(model.modelRoyaltyPercent) > 10) && (
                                     <p className="text-red-500 text-xs">Max 10% royalties.</p>
                                   )}
                                   {model.modelRoyaltyPercent !== undefined && Number(model.modelRoyaltyPercent) >= 0 && Number(model.modelRoyaltyPercent) <= 10 && (
                                     <p className="text-green-500 text-xs">{model.modelRoyaltyPercent}% on resales</p>
                                   )}
                                 </div>
                               ) : (
                                 <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center">
                                   {model.modelRoyaltyPercent !== undefined ? `${model.modelRoyaltyPercent}%` : '0%'}
                                 </div>
                               )}
                             </div>

                             {/* Model Tags */}
                             <div className="space-y-1">
                               <label className="block text-xs font-medium text-gray-400">
                                 Tags
                               </label>
                               
                               {/* Tags Display */}
                               {model.modelTags && model.modelTags.length > 0 && (
                                 <div className="flex flex-wrap gap-1 mb-2">
                                   {model.modelTags.map((tag, tagIndex) => (
                                     <span
                                       key={tagIndex}
                                       className="inline-flex items-center gap-1 px-2 py-1 bg-white/20 text-white rounded-full text-xs"
                                     >
                                       {tag}
                                       {editingModel === index && (
                                         <button
                                           type="button"
                                           onClick={() => removeModelTag(index, tag)}
                                           className="text-white hover:text-gray-300 transition-colors"
                                         >
                                           ×
                                         </button>
                                       )}
                                     </span>
                                   ))}
                                 </div>
                               )}
                               
                               {/* Tag Input - Only show when editing */}
                               {editingModel === index && (
                                 <div className="flex gap-1">
                                   <input
                                     type="text"
                                     value={newTag}
                                     onChange={(e) => setNewTag(e.target.value)}
                                     onKeyPress={(e) => handleModelTagKeyPress(e, index)}
                                     placeholder={model.modelTags && model.modelTags.length >= 5 ? "Maximum 5 tags reached" : "Add a tag..."}
                                     disabled={model.modelTags && model.modelTags.length >= 5}
                                     className={`flex-1 px-2 py-1 bg-[#1a1a1a] border border-[#3a3a3a] rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-white focus:border-transparent ${
                                       model.modelTags && model.modelTags.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
                                     }`}
                                   />
                                   <button
                                     type="button"
                                     onClick={() => addModelTag(index)}
                                     disabled={model.modelTags && model.modelTags.length >= 5}
                                     className={`px-2 py-1 rounded text-xs transition-all duration-200 font-medium ${
                                       model.modelTags && model.modelTags.length >= 5 
                                         ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                                         : 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white hover:scale-105'
                                     }`}
                                   >
                                     Add
                                   </button>
                                 </div>
                               )}
                               
                               {/* Show tags when not editing */}
                               {editingModel !== index && (!model.modelTags || model.modelTags.length === 0) && (
                                 <div className="px-3 py-2 bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg text-white text-sm min-h-[40px] flex items-center text-gray-500">
                                   No tags set
                                 </div>
                               )}
                             </div>
                          </div>
                          
                          {/* File Details */}
                          <div className="space-y-2 text-sm mb-4">
                            <div className="flex justify-between text-gray-400">
                              <span>Size:</span>
                              <span className="text-white">{(model.file.size / 1024 / 1024).toFixed(2)} MB</span>
                            </div>
                            <div className="flex justify-between text-gray-400">
                              <span>Type:</span>
                              <span className="text-white">{model.file.type || 'GLB'}</span>
                            </div>
                            <div className="flex justify-between text-gray-400">
                              <span>Status:</span>
                              <span className="text-green-400 font-medium">Ready</span>
                            </div>
                          </div>
                          
                                                     {/* Action Buttons */}
                           <div className="flex gap-1.5 justify-center flex-wrap">
                             {editingModel === index ? (
                               <>
                                 <button
                                   type="button"
                                   onClick={() => handleModelSave(index)}
                                   className="px-2.5 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all duration-200 text-xs font-medium hover:scale-105 flex items-center gap-1.5 flex-shrink-0"
                                 >
                                   <Save size={12} />
                                   Save
                                 </button>
                                 <button
                                   type="button"
                                   onClick={handleModelCancel}
                                   className="px-2.5 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all duration-200 text-xs font-medium hover:scale-105 flex items-center gap-1.5 flex-shrink-0"
                                 >
                                   <XCircle size={12} />
                                   Cancel
                                 </button>
                               </>
                             ) : (
                                                              <button
                                  type="button"
                                  onClick={() => handleModelEdit(index)}
                                  className="px-2.5 py-1.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg transition-all duration-200 text-xs font-medium hover:scale-105 flex items-center gap-1.5 flex-shrink-0"
                                >
                                  <Edit3 size={12} />
                                  Edit
                                </button>
                             )}
                             <button
                               type="button"
                               onClick={() => removeModel(index)}
                               className="px-2.5 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 text-xs font-medium hover:scale-105 flex items-center gap-1.5 flex-shrink-0"
                             >
                               <Trash2 size={12} />
                               Remove
                             </button>
                           </div>
                        </div>
                      ))}
                    </div>
                    
                                         {/* Capsule Summary */}
                     <div className="bg-[#0a0a0a] rounded-xl p-6 border border-[#2a2a2a]">
                       <h4 className="text-white font-semibold text-lg mb-4">Capsule Summary</h4>
                       <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                         <div className="text-center">
                           <div className="text-2xl font-bold text-white">{collectionModels.length}</div>
                           <div className="text-gray-400">Pieces</div>
                         </div>
                         <div className="text-center">
                           <div className="text-2xl font-bold text-blue-400">
                             {(collectionModels.reduce((total, model) => total + model.file.size, 0) / 1024 / 1024).toFixed(2)}
                           </div>
                           <div className="text-gray-400">Total Size (MB)</div>
                         </div>
                         <div className="text-center">
                           <div className="text-2xl font-bold text-green-400">
                             {collectionModels.reduce((total, model) => total + parseInt(model.modelQuantity || 1), 0)}
                           </div>
                           <div className="text-gray-400">Total Quantity</div>
                         </div>
                         <div className="text-center">
                           <div className="text-2xl font-bold text-orange-400">
                             {(() => {
                               const piecesWithPrices = collectionModels.filter(model => model.modelPrice && parseFloat(model.modelPrice) > 0);
                               if (piecesWithPrices.length === 0) return '0.00';
                               const totalPrice = piecesWithPrices.reduce((sum, model) => sum + parseFloat(model.modelPrice), 0);
                               return (totalPrice / piecesWithPrices.length).toFixed(2);
                             })()}
                           </div>
                           <div className="text-gray-400">Avg Price (SOL)</div>
                         </div>
                       </div>
                      
                      {/* Models with Names Count */}
                      <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                                                 <div className="text-center">
                           <div className="text-lg font-medium text-white mb-2">Pieces Ready for Sale</div>
                           <div className="text-2xl font-bold text-white">
                             {collectionModels.filter(model => model.modelName && model.modelName.trim()).length}
                           </div>
                           <div className="text-gray-400 text-sm">
                             of {collectionModels.length} pieces have names set
                           </div>
                         </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fee Estimation */}
            {collectionModels.length > 0 && (() => {
              // Calculate total pieces across all models
              const totalPieces = collectionModels.reduce((sum, model) => {
                return sum + (parseInt(model.modelQuantity) || 0);
              }, 0);
              
              if (totalPieces === 0) return null;
              
              const feePerPiece = 0.004;
              const totalFee = totalPieces * feePerPiece;
              const hasInsufficientBalance = walletBalance !== null && walletBalance < totalFee;
              
              return (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Estimated Minting Fees</h3>
                  
                  <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-2xl p-6 border border-[#2a2a2a]">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                        <Zap className="text-blue-400" size={24} />
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <h4 className="text-white font-semibold mb-1">Wallet Transaction Fees</h4>
                          <p className="text-gray-400 text-sm">
                            You'll pay these fees directly from your wallet when minting. These cover blockchain transaction costs and account creation.
                          </p>
                        </div>
                        
                        {/* Fee Breakdown */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-2 border-b border-[#2a2a2a]">
                            <span className="text-gray-300 text-sm">Total pieces to mint</span>
                            <span className="text-white font-medium">{totalPieces}</span>
                          </div>
                          
                          <div className="flex justify-between items-center py-2 border-b border-[#2a2a2a]">
                            <span className="text-gray-300 text-sm">Cost per piece</span>
                            <div className="text-right">
                              <div className="text-white font-medium">~0.004 SOL</div>
                              <div className="text-xs text-gray-400">
                                ≈ ${(0.004 * solPrice).toFixed(2)} USD
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center py-3 bg-[#0a0a0a] rounded-xl px-4 mt-3">
                            <span className="text-white font-semibold">Estimated Total</span>
                            <div className="text-right">
                              <div className="text-white font-bold text-lg">
                                ~{totalFee.toFixed(4)} SOL
                              </div>
                              <div className="text-sm text-gray-400">
                                ≈ ${(totalFee * solPrice).toFixed(2)} USD
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Info Message */}
                        <div className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/20 rounded-xl p-3">
                          <AlertTriangle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                          <p className="text-blue-300 text-xs">
                            This is an estimate. Actual fees may vary slightly based on network conditions. 
                            Includes transaction fees, token account creation, and metadata storage.
                          </p>
                        </div>
                        
                        {/* Wallet Balance Check */}
                        {walletBalance !== null && (
                          <div className={`flex items-start gap-2 rounded-xl p-3 ${
                            hasInsufficientBalance 
                              ? 'bg-red-500/10 border border-red-500/30' 
                              : 'bg-green-500/10 border border-green-500/30'
                          }`}>
                            {hasInsufficientBalance ? (
                              <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                            ) : (
                              <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
                            )}
                            <div className="flex-1">
                              <p className={`text-xs ${hasInsufficientBalance ? 'text-red-300' : 'text-green-300'}`}>
                                {hasInsufficientBalance ? (
                                  <>
                                    <span className="font-semibold">Insufficient balance.</span> You have {walletBalance.toFixed(4)} SOL but need {totalFee.toFixed(4)} SOL to mint. The mint button is disabled until you add more SOL to your wallet.
                                  </>
                                ) : (
                                  <>
                                    <span className="font-semibold">Balance OK.</span> You have {walletBalance.toFixed(4)} SOL available ({(walletBalance - totalFee).toFixed(4)} SOL remaining after minting).
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Submit Button */}
            <div className="flex justify-end items-center pt-8 border-t border-[#2a2a2a]">
              <button
                 type="submit"
                 disabled={
                   isCreating ||
                   !formData.name ||
                   !formData.description ||
                   !formData.banner ||
                   !formData.website ||
                   !formData.twitter ||
                   !formData.instagram ||
                   collectionModels.length === 0 ||
                   collectionModels.some(model => 
                     !model.modelName?.trim() || 
                     !model.modelDescription?.trim() || 
                     !model.modelColor?.trim() || 
                     !model.modelCategory?.trim() || 
                     !model.modelPrice || 
                     parseFloat(model.modelPrice) <= 0 ||
                     !model.modelQuantity || 
                     parseInt(model.modelQuantity) < 1 ||
                     !model.thumbnail || 
                     !model.thumbnail.dataUrl
                   ) ||
                   (walletBalance !== null && collectionModels.length > 0 && walletBalance < (collectionModels.reduce((sum, model) => sum + (parseInt(model.modelQuantity) || 0), 0) * 0.004))
                 }
                 className="px-10 py-4 bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:from-blue-700 active:to-blue-800 border border-blue-400/30 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 font-semibold text-lg flex items-center gap-3 hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-blue-500/25 disabled:hover:scale-100 disabled:shadow-none"
               >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                    Creating & Minting...
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <Plus size={20} />
                    Mint Capsule
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
              </div>
      </div>

      {/* Mint Progress Modal (like execute trading page) */}
      {mintProgressModal.show && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
          <div className="bg-[#111111] border-2 border-gray-600/50 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl shadow-gray-500/10 max-h-[80vh] overflow-y-auto">
            {!mintProgressModal.error && !mintProgressModal.isComplete ? (
              <>
                {/* Loading State */}
                <div className="p-8">
                  <div className="flex flex-col items-center">
                    {/* Animated 3D Logo */}
                    <div className="relative w-24 h-24 mb-6">
                      <Logo3D 
                        className="w-full h-full"
                        autoRotate={true}
                        baseRotationSpeed={0.03}
                        enableControls={false}
                        showEnvironment={true}
                        cameraPosition={[0, 0, 3.5]}
                        fov={50}
                        loadingMode={true}
                      />
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white mb-2">Minting Capsule</h3>
                    <p className="text-gray-400 text-center mb-6">{mintProgressModal.currentMessage}</p>
                    
                    {/* Progress Bar */}
                    <div className="w-full max-w-md mb-6">
                      <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
                        <span>Progress</span>
                        <span>{mintProgressModal.step} / {mintProgressModal.totalSteps} pieces ({Math.round((mintProgressModal.step / mintProgressModal.totalSteps) * 100)}%)</span>
                      </div>
                      <div className="w-full bg-[#2a2a2a] rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${(mintProgressModal.step / mintProgressModal.totalSteps) * 100}%` }}
                        ></div>
                      </div>
                      {/* Estimated time remaining for large batches */}
                      {mintProgressModal.totalSteps > 100 && mintProgressModal.step > 0 && (
                        <div className="text-xs text-gray-500 mt-2 text-center">
                          {(() => {
                            const avgTimePerPiece = mintProgressModal.totalSteps > 100 ? 1.2 : 2.5; // seconds
                            const remainingPieces = mintProgressModal.totalSteps - mintProgressModal.step;
                            const estimatedSeconds = Math.ceil(remainingPieces * avgTimePerPiece);
                            const minutes = Math.floor(estimatedSeconds / 60);
                            const seconds = estimatedSeconds % 60;
                            return `Est. time remaining: ${minutes}m ${seconds}s`;
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Pieces Progress */}
                    <div className="w-full max-w-md space-y-2 mb-6 max-h-60 overflow-y-auto">
                      {mintProgressModal.piecesProgress.map((piece, index) => (
                        <div key={index} className="flex items-center gap-3 text-sm">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                            piece.status === 'success' && !piece.needsManualListing
                              ? 'bg-green-500/20 border-2 border-green-500' 
                              : piece.status === 'success' && piece.needsManualListing
                              ? 'bg-yellow-500/20 border-2 border-yellow-500'
                              : piece.status === 'minting'
                              ? 'bg-gradient-to-r from-gray-300/20 to-gray-100/20 border-2 border-gray-300'
                              : piece.status === 'error'
                              ? 'bg-red-500/20 border-2 border-red-500'
                              : 'bg-[#2a2a2a] border-2 border-[#2a2a2a]'
                          }`}>
                            {piece.status === 'success' && !piece.needsManualListing ? (
                              <CheckCircle size={14} className="text-green-400" />
                            ) : piece.status === 'success' && piece.needsManualListing ? (
                              <AlertCircle size={14} className="text-yellow-400" />
                            ) : piece.status === 'minting' ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : piece.status === 'error' ? (
                              <XCircle size={14} className="text-red-400" />
                            ) : (
                              <span className="text-gray-600 text-xs font-bold">{index + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium transition-colors truncate ${
                              piece.status === 'success' && !piece.needsManualListing ? 'text-green-400' :
                              piece.status === 'success' && piece.needsManualListing ? 'text-yellow-400' :
                              piece.status === 'minting' ? 'text-white' :
                              piece.status === 'error' ? 'text-red-400' :
                              'text-gray-600'
                            }`}>
                              {piece.name}
                              {piece.needsManualListing && (
                                <span className="text-yellow-400/70 text-[10px] ml-1">(needs listing)</span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Info Text */}
                    <p className="text-gray-500 text-xs mt-4 text-center">
                      Please don't close this window. This may take a few moments...
                    </p>
                  </div>
                </div>
              </>
            ) : mintProgressModal.isComplete ? (
              <>
                {/* Success State */}
                <div className="p-8">
                  <div className="flex flex-col items-center">
                    {/* Success Icon */}
                    <div className="relative w-24 h-24 mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 rounded-full opacity-30"></div>
                      <div className="absolute inset-2 bg-[#111111] rounded-full flex items-center justify-center">
                        <CheckCircle size={48} className="text-green-400" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white mb-2">Capsule Minted! 🎉</h3>
                    <p className="text-gray-400 text-center mb-6">
                      Successfully minted {mintProgressModal.totalSuccess} of {mintProgressModal.totalSteps} pieces
                    </p>
                    
                    {/* Transaction Summary */}
                    <div className="w-full bg-[#0a0a0a] rounded-xl p-4 mb-6 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Successful</span>
                        <span className="text-green-400 font-medium">{mintProgressModal.totalSuccess}</span>
                      </div>
                      {mintProgressModal.totalFailed > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Failed</span>
                          <span className="text-red-400 font-medium">{mintProgressModal.totalFailed}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Manual Listing Notice */}
                    {mintProgressModal.piecesProgress.some(p => p.needsManualListing) && (
                      <div className="w-full bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <AlertCircle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-yellow-400 font-medium text-sm mb-1">Some pieces need manual listing</p>
                            <p className="text-yellow-400/70 text-xs leading-relaxed">
                              {mintProgressModal.piecesProgress.filter(p => p.needsManualListing).length} piece(s) 
                              minted successfully but weren't auto-listed. You can list them from your Listings page.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Action Button */}
                    <button
                      onClick={() => {
                        setMintProgressModal({ ...mintProgressModal, show: false });
                        navigate('/profile/created');
                      }}
                      className="w-full px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-medium transition-colors"
                    >
                      View My Capsules
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Error State */}
                <div className="p-8">
                  <div className="flex flex-col items-center">
                    {/* Error Icon */}
                    <div className="relative w-24 h-24 mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full opacity-20"></div>
                      <div className="absolute inset-2 bg-[#111111] rounded-full flex items-center justify-center">
                        <XCircle size={48} className="text-red-400" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white mb-2">Minting Failed</h3>
                    <p className="text-gray-400 text-center mb-6">
                      {mintProgressModal.error || 'An error occurred while minting your capsule'}
                    </p>
                    
                    {/* Action Button */}
                    <button
                      onClick={() => setMintProgressModal({ ...mintProgressModal, show: false })}
                      className="w-full px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <Check className="w-5 h-5 text-green-400" />
                ) : notification.type === 'error' ? (
                  <X className="w-5 h-5 text-red-400" />
                ) : (
                  <Eye className="w-5 h-5 text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </WalletRequired>
  );
};

export default CreateCollection;
